import { useState, useEffect } from "react";
import { 
  Box, Grid, Typography, Link, List, ListItem, Divider,
  TextField, InputAdornment, Chip, Stack, Button, Paper
} from "@mui/material";
import { Search, Category, ExpandMore, ExpandLess } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import coursesDB from "../database/CoursesProducts";

const CourseList = () => {
  const [draggedOverIndex, setDraggedOverIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategories, setShowCategories] = useState(false);

  // Get unique categories
  const allCategories = [...new Set(coursesDB.map(cat => cat.category))];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Toggle category filter
  const handleCategoryClick = (category) => {
    setSelectedCategory(prev => prev === category ? null : category);
  };

  // Filter courses
  const filteredCategories = coursesDB
    .filter(category => !selectedCategory || category.category === selectedCategory)
    .map(category => ({
      ...category,
      courses: category.courses.filter(course =>
        course.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }))
    .filter(category => category.courses.length > 0);

  const handleDragEnter = (index) => {
    setDraggedOverIndex(index);
  };

  const handleDragLeave = () => {
    setDraggedOverIndex(null);
  };

  return (
    <Box sx={{ 
      padding: { xs: 4, md: 8 }, 
      background: 'linear-gradient(135deg, #f5f7fa, #c3cfe2)',
      minHeight: '100vh',
      fontFamily: '"Montserrat", sans-serif',
    }}>
      {/* Header */}
      <Typography variant="h3" sx={{ 
        fontWeight: 700, 
        mb: 4,
        color: '#2c3e50',
        textAlign: 'center',
        fontSize: { xs: '2rem', md: '2.5rem' },
        textTransform: 'uppercase',
        letterSpacing: '2px',
      }}>
        1000+ Featured Courses 🚀
      </Typography>

      {/* Category Filters */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setShowCategories(!showCategories)}
          sx={{
            mb: 2,
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1.1rem',
            fontWeight: 600,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          endIcon={showCategories ? <ExpandLess /> : <ExpandMore />}
        >
          <Category sx={{ mr: 1, verticalAlign: 'middle' }} />
          {selectedCategory ? `Selected: ${selectedCategory}` : "Filter by Category"}
        </Button>

        {showCategories && (
          <Paper elevation={3} sx={{ 
            p: 2, 
            borderRadius: 2, 
            maxWidth: 800, 
            mx: 'auto',
            bgcolor: 'background.paper'
          }}>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              {allCategories.map(category => (
                <Chip
                  key={category}
                  label={category}
                  clickable
                  variant={selectedCategory === category ? "filled" : "outlined"}
                  color="primary"
                  onClick={() => handleCategoryClick(category)}
                  sx={{ 
                    mb: 1,
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'scale(1.05)' },
                    fontWeight: selectedCategory === category ? 700 : 500
                  }}
                />
              ))}
            </Stack>
          </Paper>
        )}
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={`Search in ${selectedCategory || "all categories"}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 4,
              bgcolor: 'background.paper',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main'
              }
            }
          }}
        />
      </Box>

      {/* Course List */}
      <Grid container spacing={4} justifyContent="center">
        {filteredCategories.length > 0 ? (
          filteredCategories.map((category, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Box sx={{
                backgroundColor: '#ffffff',
                borderRadius: 4,
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
                },
              }}>
                {/* Category Header */}
                <Box sx={{
                  background: 'linear-gradient(45deg, #2c3e50, #3498db)',
                  padding: 2,
                  textAlign: 'center',
                }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 600, 
                    color: '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}>
                    {category.category}
                  </Typography>
                </Box>

                {/* Course List */}
                <List sx={{ width: '100%', padding: 0 }}>
                  {category.courses.map((course, idx) => (
                    <div key={course.link}>
                      <ListItem 
                        sx={{ 
                          padding: 2,
                          transition: 'all 0.3s ease',
                          backgroundColor: draggedOverIndex === idx ? '#f0f0f0' : 'inherit',
                          '&:hover': {
                            backgroundColor: '#f8f9fa',
                          }
                        }}
                        onDragEnter={() => handleDragEnter(idx)}
                        onDragLeave={handleDragLeave}
                      >
                        <Link
                          component={RouterLink}
                          to={course.link}
                          sx={{
                            width: '100%',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          <Typography variant="h6" sx={{
                            fontWeight: 500,
                            color: 'text.primary',
                            lineHeight: 1.6,
                            fontSize: '1.1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                          }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                backgroundColor: 'primary.main',
                                borderRadius: '50%',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                              }}
                            />
                            {course.name}
                          </Typography>
                        </Link>
                      </ListItem>
                      {idx < category.courses.length - 1 && <Divider sx={{ my: 1 }} />}
                    </div>
                  ))}
                </List>
              </Box>
            </Grid>
          ))
        ) : (
          <Typography variant="h6" sx={{
            color: 'text.secondary',
            textAlign: 'center',
            width: '100%',
            mt: 4
          }}>
            {selectedCategory 
              ? `No courses found in "${selectedCategory}" matching "${searchTerm}"`
              : `No courses found matching "${searchTerm}"`}
          </Typography>
        )}
      </Grid>
    </Box>
  );
};

export default CourseList;